import { useState } from "react";
import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import { Menu, X, Settings, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/", label: "Tableau de bord", id: "nav-dashboard" },
  { to: "/sponsors", label: "Sponsors", id: "nav-sponsors" },
  { to: "/bilans", label: "Bilans", id: "nav-bilans" },
];

const linkCls = ({ isActive }) =>
  `px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/75 hover:text-white"}`;

export const Logo = ({ light = true }) => (
  <span className="flex items-center gap-2.5">
    <span className="grid place-items-center w-8 h-8 rounded-lg bg-[#3DDC84] text-[#13201A] font-display font-bold">P</span>
    <span className={`font-display font-bold text-lg ${light ? "text-white" : "text-[#13201A]"}`}>Partenia</span>
  </span>
);

function NavBar() {
  const { session, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const club = session.club;
  const doLogout = async () => { await logout(); nav("/connexion"); };
  return (
    <header className="bg-[#13201A] no-print sticky top-0 z-40">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center gap-6">
        <Link to="/" data-testid="nav-logo"><Logo /></Link>
        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => <NavLink key={l.to} to={l.to} end={l.to === "/"} className={linkCls} data-testid={l.id}>{l.label}</NavLink>)}
        </nav>
        <div className="ml-auto hidden md:flex items-center gap-4">
          <span data-testid="nav-club-info" className="text-sm text-white/80">{club.name} · Saison {club.season}</span>
          <NavLink to="/parametres" className="text-white/70 hover:text-white" title="Paramètres du club" data-testid="nav-settings"><Settings size={18} /></NavLink>
          <button onClick={doLogout} className="text-white/70 hover:text-white" title="Déconnexion" data-testid="nav-logout"><LogOut size={18} /></button>
        </div>
        <button className="ml-auto md:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/10 px-4 pb-4 pt-2 flex flex-col gap-1" onClick={() => setOpen(false)}>
          <span className="text-xs text-white/60 px-3.5 py-1">{club.name} · Saison {club.season}</span>
          {LINKS.map((l) => <NavLink key={l.to} to={l.to} end={l.to === "/"} className={linkCls} data-testid={`${l.id}-mobile`}>{l.label}</NavLink>)}
          <NavLink to="/parametres" className={linkCls} data-testid="nav-settings-mobile">Paramètres du club</NavLink>
          <button onClick={doLogout} className="text-left px-3.5 py-2 text-sm font-semibold text-white/75" data-testid="nav-logout-mobile">Déconnexion</button>
        </div>
      )}
    </header>
  );
}

export const Footer = () => (
  <footer className="no-print max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 py-8 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-[#5B665F]">
    <span>© {new Date().getFullYear()} Partenia</span>
    <Link to="/mentions-legales" className="hover:text-[#13201A]" data-testid="footer-legal-link">Mentions légales</Link>
    <Link to="/confidentialite" className="hover:text-[#13201A]" data-testid="footer-privacy-link">Politique de confidentialité</Link>
  </footer>
);

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-1 w-full max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-10 print-page">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
